import type {
	CamposFiscaisManuaisDigitacao,
	TipoDeTransacaoDeVenda,
	TipoNota,
} from '@/models/venda/Venda'
import type { Imposto, CST } from '@/models/fiscal/Imposto'
import type { RegraImpostoBase, RegraPorEstado } from '@/models/fiscal/RegraPorEstado'
import type { Operacao } from '@/models/fiscal/RegraDeEstado'

export interface DadosVendaParaCfopDaRegra {
	tipoDeTransacao: TipoDeTransacaoDeVenda
	tipoNota: TipoNota | null | undefined
	isConsignado: boolean
	isDemonstracao: boolean
	pontoDeVenda: { vendaForaDoEstabelecimento?: boolean } | null
}

function listaImpostos(impostos: RegraPorEstado['impostos']): Imposto[] {
	if (!impostos) return []
	return Array.isArray(impostos) ? impostos : Object.values(impostos)
}

function cstPreferindoOperacao(imposto: Imposto | undefined, operacao: Operacao): CST | null {
	if (!imposto?.csts?.length) return null
	const match = imposto.csts.find(c => c.operacao === operacao)
	return (match ?? imposto.csts[0])?.cst ?? null
}

function vendaForaDoEstabelecimentoEhTrue(
	pdv: DadosVendaParaCfopDaRegra['pontoDeVenda'],
): boolean {
	return pdv != null && pdv.vendaForaDoEstabelecimento === true
}

function tipoNotaEntradaOuNull(tipoNota: TipoNota | null | undefined): boolean {
	return tipoNota == null || tipoNota === 'Entrada'
}

function tipoNotaSaida(tipoNota: TipoNota | null | undefined): boolean {
	return tipoNota === 'Saída'
}

export function compararOperacaoFiscalDaVenda(
	operacaoFiscal: Operacao,
	venda: DadosVendaParaCfopDaRegra,
): boolean {
	const transacao = venda.tipoDeTransacao
	const cons = venda.isConsignado
	const demo = venda.isDemonstracao
	const pdv = venda.pontoDeVenda
	const foraEstabelecimento = () => vendaForaDoEstabelecimentoEhTrue(pdv)
	const saidaPadraoSemFora = pdv == null || !foraEstabelecimento()

	return (
		(operacaoFiscal === 'SAIDA'
			&& transacao === 'Venda'
			&& !cons
			&& !demo
			&& saidaPadraoSemFora)
		|| (operacaoFiscal === 'DEVOLUCAO'
			&& transacao === 'Devolução'
			&& !cons
			&& !demo
			&& tipoNotaEntradaOuNull(venda.tipoNota))
		|| (operacaoFiscal === 'CONSIGNACAO' && transacao === 'Venda' && cons)
		|| (operacaoFiscal === 'DEVOLUCAO_CONSIGNACAO' && transacao === 'Devolução' && cons)
		|| (operacaoFiscal === 'VENDA_DEMONSTRACAO' && transacao === 'Venda' && demo)
		|| (operacaoFiscal === 'DEVOLUCAO_DEMONSTRACAO' && transacao === 'Devolução' && demo)
		|| (operacaoFiscal === 'TRANSFERENCIA' && transacao === 'Transferência')
		|| (operacaoFiscal === 'SAIDA_DEVOLUCAO'
			&& transacao === 'Devolução'
			&& tipoNotaSaida(venda.tipoNota))
		|| (operacaoFiscal === 'OUTROS_ENTRADA'
			&& transacao === 'Outros'
			&& venda.tipoNota === 'Entrada')
		|| (operacaoFiscal === 'OUTROS_SAIDA'
			&& transacao === 'Outros'
			&& tipoNotaSaida(venda.tipoNota))
		|| (operacaoFiscal === 'VENDA_FORA_ESTABELECIMENTO'
			&& transacao === 'Venda'
			&& tipoNotaSaida(venda.tipoNota)
			&& foraEstabelecimento())
		|| (operacaoFiscal === 'RETORNO_CONSERTO'
			&& transacao === 'Conserto'
			&& venda.tipoNota === 'Entrada')
		|| (operacaoFiscal === 'SAIDA_CONSERTO'
			&& transacao === 'Conserto'
			&& tipoNotaSaida(venda.tipoNota))
		|| (operacaoFiscal === 'ATIVO_IMOBILIZADO'
			&& transacao === 'Ativo'
			&& tipoNotaSaida(venda.tipoNota))
		|| (operacaoFiscal === 'CONSUMO'
			&& transacao === 'Consumo'
			&& tipoNotaSaida(venda.tipoNota))
		|| (operacaoFiscal === 'UNIFORME'
			&& transacao === 'Uniforme'
			&& venda.tipoNota === 'Entrada')
		|| (operacaoFiscal === 'SAIDA_UNIFORME' && tipoNotaSaida(venda.tipoNota))
	)
}

function cfopDaRegra(regra: RegraPorEstado, venda: DadosVendaParaCfopDaRegra): string {
	const tipos = regra.tiposDeOperacoesFiscais ?? []
	const operacao = tipos.find(t => compararOperacaoFiscalDaVenda(t.operacao, venda))
	const cfop = operacao?.cfop
	return cfop?.codigo ? String(cfop.codigo) : ''
}

export function regraPorEstadoParaCamposFiscaisManuaisDigitacao(
	regraImpostoBase: RegraImpostoBase,
	venda: DadosVendaParaCfopDaRegra,
): Partial<CamposFiscaisManuaisDigitacao> {
	const regra = regraImpostoBase.regraPorEstado

	const impostos = listaImpostos(regra.impostos)
	const porTipo = (t: Imposto['tipoDeImposto']) => impostos.find(i => i.tipoDeImposto === t)

	const icms = porTipo('ICMS')
	const pis = porTipo('PIS')
	const cofins = porTipo('COFINS')
	const ipi = regraImpostoBase.ipi
	const ibs = porTipo('IBS')
	const cbs = porTipo('CBS')

	const opSaida: Operacao = 'SAIDA'
	const cfop = cfopDaRegra(regra, venda)

	const patch: Partial<CamposFiscaisManuaisDigitacao> = {
		reduzIcms: regra.reduzIcms,
		incluirFrete: regra.incluirFrete,
		reduzDifal: regra.reduzDifal,
		zerarBaseCalculoIcms: regra.zerarBaseCalculoIcms,
		utilizarCstPisCofinsOrigem: regra.utilizarCstPisCofinsOrigem,
		reduzImpostosBcIBSCBS: regra.reduzImpostosBcIBSCBS,
		codigosDeBeneficio: regra.codigosDeBeneficio ?? '',
		codigoCreditoPresumido: regra.codigoCreditoPresumido ?? '',
		aliquotaCreditoPresumido: regra.aliquotaCreditoPresumido ?? 0,
		aliquotaDifICMS: regra.aliquotaDifICMS ?? 0,
		codigoClassificacaoTributaria: regra.codigoClassificacaoTributaria ?? null,
		aliquotaFcp: regra.percentualFCP ?? 0,
		origem: regraImpostoBase.origem,
		aliquotaMva: regra.percentualMVA ?? 0,
		cstIcms: cstPreferindoOperacao(icms, opSaida),
		aliquotaIcms: icms?.aliquota ?? 0,
		cstPis: cstPreferindoOperacao(pis, opSaida),
		aliquotaPis: pis?.aliquota ?? 0,
		cstCofins: cstPreferindoOperacao(cofins, opSaida),
		aliquotaCofins: cofins?.aliquota ?? 0,
		cstIpi: cstPreferindoOperacao(ipi, opSaida),
		aliquotaIpi: ipi?.aliquota ?? 0,
		cstIBSCBS: cstPreferindoOperacao(ibs, opSaida),
		aliquotaIBS: ibs?.aliquota ?? 0,
		aliquotaCBS: cbs?.aliquota ?? 0,
	}

	if (cfop) patch.cfop = cfop

	return patch
}
